import axios from "axios";
import { configData } from "./api-config";
import store from "../store";
import authSession from "./authSession";
import { setRefreshToken } from "../store/actions/refreshTokenAction";

const apiClient = axios.create({ baseURL: configData.BASE_URL + "api/" });
const cancelled = () => new axios.CanceledError("Session changed");
const ownsRequest = (request, session) => request._authSessionId === session.syncFromStorage().sessionId;

const normalizeApiError = (error) => {
  if (!error?.response || error.__CANCEL__) return error;
  const body = error.response.data;
  // Keep existing errorText/errors consumers compatible while retaining the HTTP status.
  return {
    ...(body && typeof body === "object" ? body : { message: body || error.message }),
    httpStatus: error.response.status,
  };
};

export const createAuthRequestHandler = (session) => (config) => {
  const headers = { ...config.headers };
  if (config.skipAuth) {
    delete headers.Authorization;
    return { ...config, headers };
  }
  // Capture before syncing: a stale tab must not send its old form under B's token.
  const expectedSessionId =
    config._authSessionId === undefined ? session.getSnapshot().sessionId : config._authSessionId;
  const current = session.syncFromStorage();
  if (expectedSessionId !== current.sessionId) throw cancelled();
  if (current.token) headers.Authorization = "Bearer " + current.token;
  else delete headers.Authorization;
  return { ...config, headers, _authSessionId: current.sessionId, _authToken: current.token };
};

export const createAuthResponseHandler = (session) => (response) => {
  if (response.config && !response.config.skipAuth && !ownsRequest(response.config, session)) {
    throw cancelled();
  }
  return response;
};

export const createAuthResponseErrorHandler = ({
  apiClient,
  refreshClient,
  refreshUrl,
  authStore,
  session,
  location,
}) => {
  let refresh = null;
  let redirectingSessionId = null;
  const redirectToLogin = (request) => {
    if (!ownsRequest(request, session)) throw cancelled();
    if (redirectingSessionId !== request._authSessionId) {
      redirectingSessionId = request._authSessionId;
      session.endSession();
      location.replace("/login?unauthorized=1");
    }
    return new Promise(() => {});
  };
  const refreshAccessToken = (sessionId, previousToken) => {
    if (!refresh || refresh.sessionId !== sessionId) {
      const pending = { sessionId, promise: null };
      refresh = pending;
      authStore.dispatch({ ...setRefreshToken(true), meta: { sessionId } });
      pending.promise = refreshClient
        .post(refreshUrl, null, { withCredentials: true })
        .then((response) => {
          if (session.syncFromStorage().sessionId !== sessionId) throw cancelled();
          session.replaceAccessToken(sessionId, previousToken, response.data.token);
          return session.getSnapshot().token;
        })
        .finally(() => {
          if (refresh === pending) {
            refresh = null;
            authStore.dispatch({ ...setRefreshToken(false), meta: { sessionId } });
          }
        });
    }
    return refresh.promise;
  };

  return async (error) => {
    const request = error.config;
    if (!request || request.skipAuth) return Promise.reject(normalizeApiError(error));
    if (!ownsRequest(request, session)) throw cancelled();
    const excluded = request.url?.includes("login") || request.url?.includes("session");
    if (error.response?.status !== 401 || excluded || !request._authSessionId) {
      return Promise.reject(normalizeApiError(error));
    }
    if (request._retry) return redirectToLogin(request);
    request._retry = true;
    const current = session.getSnapshot();
    try {
      // A concurrent request may have already refreshed this session.
      const token =
        current.token !== request._authToken
          ? current.token
          : await refreshAccessToken(current.sessionId, current.token);
      if (!ownsRequest(request, session)) throw cancelled();
      request.headers = { ...request.headers, Authorization: "Bearer " + token };
      request._authToken = token;
      return apiClient.request(request);
    } catch (refreshError) {
      if (!ownsRequest(request, session)) throw cancelled();
      if (refreshError.response?.status === 401) {
        // A refresh in another tab may have succeeded while this one failed.
        if (session.getSnapshot().token !== current.token) {
          request.headers = { ...request.headers, Authorization: "Bearer " + session.getSnapshot().token };
          return apiClient.request(request);
        }
        return redirectToLogin(request);
      }
      return Promise.reject(normalizeApiError(refreshError));
    }
  };
};

// Synchronous stamping captures the caller's session before Axios yields.
apiClient.interceptors.request.use(
  createAuthRequestHandler(authSession),
  (error) => {
    throw error;
  },
  { synchronous: true },
);
apiClient.interceptors.response.use(
  createAuthResponseHandler(authSession),
  createAuthResponseErrorHandler({
    apiClient,
    refreshClient: axios,
    refreshUrl: configData.BASE_URL + "api/v1/users/refresh-token",
    authStore: store,
    session: authSession,
    location: window.location,
  }),
);

// Keep streaming bodies intact while sharing the app's token and 401 refresh queue.
export async function fetchWithAuth(url, init = {}) {
  const { data } = await apiClient.request({
    url,
    method: init.method || "GET",
    headers: Object.fromEntries(new Headers(init.headers)),
    data: init.body,
    signal: init.signal,
    transformRequest: [(body) => body],
    transformResponse: [(response) => response],
    adapter: async (config) => {
      const response = await fetch(url, {
        ...init,
        headers: config.headers.toJSON(),
        signal: config.signal,
        body: config.data,
      });
      const result = { data: response, status: response.status, headers: {}, config };
      if (response.status === 401) {
        await response.body?.cancel();
        throw new axios.AxiosError("Unauthorized", "ERR_BAD_REQUEST", config, null, result);
      }
      return result;
    },
  });
  return data;
}

const { get, post, put, delete: destroy } = apiClient;
export { get, post, put, destroy };
